//Built-in libraries

//Third party libraries
import { ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, IdentificationIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import OwnershipInformation from './OwnershipInformation';
import PropertyDetails from './PropertyDetails';
import PropertyManagement from './PropertyManagement';
import Review from './Review';

//STP Libraries

import type { IGetApplicationFormInfoServiceRes, IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import ApplicantInformation from './ApplicantInformation';
//Assets and Miscellaneous
import { initialValues } from './constants';
import { RentalRegistryValidationSchema } from './validationSchema';

//Component Begin
export enum Section {
  ApplicantInformation = 'applicantInformation',
  PropertyDetails = 'propertyDetails',
  OwnershipInformation = 'ownershipInformation',
  PropertyManagement = 'propertyManagement',
  Review = 'review',
}

const steps = [
  {
    name: 'Applicant Information',
    sectionId: Section.ApplicantInformation,
    Icon: IdentificationIcon,
    component: <ApplicantInformation />,
  },
  {
    name: 'Property Details',
    sectionId: Section.PropertyDetails,
    Icon: ClipboardDocumentListIcon,
    component: <PropertyDetails />,
  },
  {
    name: 'Ownership Information',
    sectionId: Section.OwnershipInformation,
    Icon: UserCircleIcon,
    component: <OwnershipInformation />,
  },
  {
    name: 'Property Management',
    sectionId: Section.PropertyManagement,
    Icon: UserCircleIcon,
    component: <PropertyManagement />,
  },
  {
    name: 'Review',
    sectionId: Section.Review,
    Icon: ClipboardDocumentCheckIcon,
    component: <Review />,
  },
];

interface ApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: ApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: data,
        files: [...(data?.propertyDetails?.additionalDocs || [])],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return <SubmitSuccess message='' />;
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData?.data?.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <div className='w-full flex justify-center'>
            <img src={formData?.data.logo} alt='Form Logo' className='h-14 xl:h-20 object-scale-down ' />
          </div>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider
            defaultValues={initialValues}
            mode='onChange'
            resolver={
              RentalRegistryValidationSchema[currentStep] ? yupResolver(RentalRegistryValidationSchema[currentStep] as any) : undefined
            }
          >
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const ApplicationForm = ({ ...props }: ApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
